import { persistReducer } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import { Reducer } from 'redux';
import { PersistPartial } from 'redux-persist/es/persistReducer';
import { put, takeLatest, call } from 'redux-saga/effects';

import { TAppActions } from '../rootDuck';
import { ActionsUnion, createAction } from '../../utils/action-helper';
import { IServerResponse } from '../../interfaces/server';
import { getResponseMessage } from '../../utils/utils';
import { 
  getSendRecieveList, 
  getTransactionByTransactionId, 
  getTransactionByUserId, 
  declineTransaction, 
  approveTransaction, 
} from '../../crud/sendRecive.crud';
import { ISendRecieveTransaction, ITransferList } from '../../interfaces/sendRecive';

// Action Types
const CLEAR = 'sendRecive/CLEAR';
const FETCH_REQUEST = 'sendRecive/FETCH_REQUEST';
const FETCH_SUCCESS = 'sendRecive/FETCH_SUCCESS';
const FETCH_FAIL = 'sendRecive/FETCH_FAIL';

const FETCH_BY_TRANSACTION_ID_REQUEST = 'sendRecive/FETCH_BY_TRANSACTION_ID_REQUEST';
const FETCH_BY_TRANSACTION_ID_SUCCESS = 'sendRecive/FETCH_BY_TRANSACTION_ID_SUCCESS';
const FETCH_BY_TRANSACTION_ID_FAIL = 'sendRecive/FETCH_BY_TRANSACTION_ID_FAIL';

const FETCH_BY_USER_ID_REQUEST = 'sendRecive/FETCH_BY_USER_ID_REQUEST';
const FETCH_BY_USER_ID_SUCCESS = 'sendRecive/FETCH_BY_USER_ID_SUCCESS';
const FETCH_BY_USER_ID_FAIL = 'sendRecive/FETCH_BY_USER_ID_FAIL';

const DECLINE_REQUEST = 'sendRecive/DECLINE_REQUEST';
const DECLINE_SUCCESS = 'sendRecive/DECLINE_SUCCESS';
const DECLINE_FAIL = 'sendRecive/DECLINE_FAIL';

const APPROVE_REQUEST = 'sendRecive/APPROVE_REQUEST';
const APPROVE_SUCCESS = 'sendRecive/APPROVE_SUCCESS';
const APPROVE_FAIL = 'sendRecive/APPROVE_FAIL';

// Initial State
export interface IInitialState {
  sendReciveData: ITransferList | undefined;
  byIdData: ISendRecieveTransaction | undefined;
  loading: boolean;
  success: boolean;
  error: string | null;
  page: number;
  size: number;
  total: number;
}

const initialState: IInitialState = {
  sendReciveData: undefined,
  byIdData: undefined,
  loading: false,
  success: false,
  error: null,
  page: 0,
  size: 20,
  total: 0,
};

// Reducer
export const reducer: Reducer<IInitialState & PersistPartial, TAppActions> = persistReducer(
  { storage, key: 'sendRecive', whitelist: ['sendReciveData'] },
  (state = initialState, action: any) => {
    switch (action.type) {
      case CLEAR:
        return { ...state, loading: false, success: false, sendReciveData: undefined, byIdData: undefined};
      case FETCH_REQUEST:
        return { ...state, sendReciveData: undefined, loading: true, error: null, success: false };
      case FETCH_SUCCESS:
        return { ...state, page: action.payload.number, size: action.payload.size, total: action.payload.totalElements, sendReciveData: action.payload, loading: false, success: true };
      case FETCH_FAIL:
        return { ...state, loading: false, error: action.payload, success: false };

      case FETCH_BY_TRANSACTION_ID_REQUEST:
        return { ...state, loading: true, error: null };
      case FETCH_BY_TRANSACTION_ID_SUCCESS:
        return { ...state, byIdData: action.payload, loading: false, error: null };
      case FETCH_BY_TRANSACTION_ID_FAIL:
        return { ...state, byIdData: undefined, loading: false, error: null };

      case FETCH_BY_USER_ID_REQUEST:
        return { ...state, loading: true, error: null };
      case FETCH_BY_USER_ID_SUCCESS:
        return { ...state, byIdData: action.payload, loading: false, error: null };
      case FETCH_BY_USER_ID_FAIL:
        return { ...state, byIdData: undefined, loading: false, error: null };

      case DECLINE_REQUEST:
        return { ...state, loading: true, error: null };
      case DECLINE_SUCCESS:
        return { ...state, loading: false, error: null };
      case DECLINE_FAIL:
        return { ...state, loading: false, error: null };

      case APPROVE_REQUEST:
        return { ...state, loading: true, error: null };
      case APPROVE_SUCCESS:
        return { ...state, loading: false, error: null };
      case APPROVE_FAIL:
        return { ...state, loading: false, error: null };
      default:
        return state;
    }
  }
);

// Actions
export const actions = {
  clearMe: () => createAction(CLEAR),
  fetchRequest: (payload: { page: number; size: number }) => createAction(FETCH_REQUEST, payload),
  fetchSuccess: (payload: IServerResponse<ITransferList>) => createAction(FETCH_SUCCESS, payload),
  fetchFail: (payload: string) => createAction(FETCH_FAIL, payload),

  fetchByTransactionIdRequest: (payload: { transactionId: string }) => createAction(FETCH_BY_TRANSACTION_ID_REQUEST, payload),
  fetchByTransactionIdSuccess: (payload: IServerResponse<ISendRecieveTransaction>) => createAction(FETCH_BY_TRANSACTION_ID_SUCCESS, payload),
  fetchByTransactionIdFail: (payload: string) => createAction(FETCH_BY_TRANSACTION_ID_FAIL, payload),

  fetchByUserIdRequest: (payload: { userId: string }) => createAction(FETCH_BY_USER_ID_REQUEST, payload),
  fetchByUserIdSuccess: (payload: IServerResponse<ISendRecieveTransaction>) => createAction(FETCH_BY_USER_ID_SUCCESS, payload),
  fetchByUserIdFail: (payload: string) => createAction(FETCH_BY_USER_ID_FAIL, payload),

  declineRequest: (payload: string) => createAction(DECLINE_REQUEST, payload),
  declineSuccess: (payload: string) => createAction(DECLINE_SUCCESS, payload),
  declineFail: (payload: string) => createAction(DECLINE_FAIL, payload),

  approveRequest: (payload: { transferId: string }) => createAction(APPROVE_REQUEST, payload),
  approveSuccess: (payload: string) => createAction(APPROVE_SUCCESS, payload),
  approveFail: (payload: string) => createAction(APPROVE_FAIL, payload),
};

export type TActions = ActionsUnion<typeof actions>;

// Sagas
function* fetchSaga({ payload }: { payload: { page: number; size: number } }) {
  try {
    const { data }: { data: IServerResponse<ITransferList> } = yield call(() => getSendRecieveList(payload));
    yield put(actions.fetchSuccess(data));
  } catch (e) {
    yield put(actions.fetchFail(getResponseMessage(e)));
  }
}

function* fetchByTransactionIdSaga({ payload }: { payload: { transactionId: string } }) {
  try {
    const { data }: { data: IServerResponse<ISendRecieveTransaction> } = yield call(() => getTransactionByTransactionId(payload.transactionId));
    yield put(actions.fetchByTransactionIdSuccess(data));
  } catch (e) {
    yield put(actions.fetchByTransactionIdFail(getResponseMessage(e)));
  }
}

function* fetchByUserIdSaga({ payload }: { payload: { userId: string } }) {
  try {
    const { data }: { data: IServerResponse<ISendRecieveTransaction> } = yield call(() => getTransactionByUserId(payload.userId));
    yield put(actions.fetchByUserIdSuccess(data));
  } catch (e) {
    yield put(actions.fetchByUserIdFail(getResponseMessage(e)));
  }
}

function* declineSaga({ payload }: { payload: string }) {
  try {
    yield call(() => declineTransaction(payload));
    yield put(actions.declineSuccess(payload));
  } catch (e) {
    yield put(actions.declineFail(getResponseMessage(e)));
  }
}


function* approveSaga({ payload }: { payload: { transferId: string } }) {
  try {
    const { data }: { data: IServerResponse<any> } = yield call(() => approveTransaction(payload.transferId));
    yield put(actions.approveSuccess(data.data));
  } catch (e) {
    yield put(actions.approveFail(getResponseMessage(e)));
  }
}

export function* saga() {
  yield takeLatest<ReturnType<typeof actions.fetchRequest>>(FETCH_REQUEST, fetchSaga);
  yield takeLatest<ReturnType<typeof actions.fetchByTransactionIdRequest>>(FETCH_BY_TRANSACTION_ID_REQUEST, fetchByTransactionIdSaga);
  yield takeLatest<ReturnType<typeof actions.fetchByUserIdRequest>>(FETCH_BY_USER_ID_REQUEST, fetchByUserIdSaga);
  yield takeLatest<ReturnType<typeof actions.declineRequest>>(DECLINE_REQUEST, declineSaga);
  yield takeLatest<ReturnType<typeof actions.approveRequest>>(APPROVE_REQUEST, approveSaga);
}